import React, { useState, useEffect } from 'react';
import { FaTimes } from 'react-icons/fa';
import { getClientes, getImoveis } from '../services/api';
import './AgendamentoModal.css';

const AgendamentoModal = ({ isOpen, onClose, onSave, agendamento = null, clientes = [], imoveis = [] }) => {
  const [formData, setFormData] = useState({
    clienteId: '',
    imovelId: '',
    dataHora: '',
    observacoes: '',
    status: 'agendado'
  });
  const [loadingClientes, setLoadingClientes] = useState(false);
  const [loadingImoveis, setLoadingImoveis] = useState(false);
  const [clientesList, setClientesList] = useState([]);
  const [imoveisList, setImoveisList] = useState([]);
  const [pesquisaCliente, setPesquisaCliente] = useState('');
  const [pesquisaImovel, setPesquisaImovel] = useState('');
  const [showClienteDropdown, setShowClienteDropdown] = useState(false);
  const [showImovelDropdown, setShowImovelDropdown] = useState(false);
  const [clienteSelecionado, setClienteSelecionado] = useState(null);
  const [imovelSelecionado, setImovelSelecionado] = useState(null);
  const [saving, setSaving] = useState(false);

  // Fechar dropdowns ao clicar fora
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest('.search-input-wrapper')) {
        setShowClienteDropdown(false);
        setShowImovelDropdown(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      // Sempre carregar clientes e imóveis quando o modal abrir
      loadClientes();
      loadImoveis();
      
      if (agendamento) {
        // Converter data_visita e horario_visita para formato datetime-local
        let dataHora = '';
        if (agendamento.data_visita && agendamento.horario_visita) {
          // Combinar data e horário: YYYY-MM-DD + HH:MM -> YYYY-MM-DDTHH:MM
          dataHora = `${agendamento.data_visita}T${agendamento.horario_visita}`;
        } else if (agendamento.dataHora) {
          // Converter de ISO para datetime-local preservando hora local
          const dateObj = new Date(agendamento.dataHora);
          if (!isNaN(dateObj.getTime())) {
            // Usar métodos que retornam valores locais, não UTC
            const year = dateObj.getFullYear();
            const month = String(dateObj.getMonth() + 1).padStart(2, '0');
            const day = String(dateObj.getDate()).padStart(2, '0');
            const hours = String(dateObj.getHours()).padStart(2, '0');
            const minutes = String(dateObj.getMinutes()).padStart(2, '0');
            dataHora = `${year}-${month}-${day}T${hours}:${minutes}`;
          }
        }
        
        const clienteId = agendamento.clienteId || agendamento.cliente_id || (agendamento.cliente?.id || '');
        const imovelId = agendamento.imovelId || agendamento.imovel_id || (agendamento.imovel?.id || '');
        
        setFormData({
          clienteId: clienteId.toString(),
          imovelId: imovelId.toString(),
          dataHora: dataHora,
          observacoes: agendamento.observacoes || '',
          status: agendamento.status || 'agendado'
        });
      } else {
        // Data/hora padrão: próxima hora
        const now = new Date();
        now.setMinutes(0);
        now.setHours(now.getHours() + 1);
        setFormData({
          clienteId: '',
          imovelId: '',
          dataHora: now.toISOString().slice(0, 16),
          observacoes: '',
          status: 'agendado'
        });
        setPesquisaCliente('');
        setPesquisaImovel('');
        setClienteSelecionado(null);
        setImovelSelecionado(null);
      }
    } else {
      // Limpar dados quando fechar o modal
      setClientesList([]);
      setImoveisList([]);
      setPesquisaCliente('');
      setPesquisaImovel('');
      setShowClienteDropdown(false);
      setShowImovelDropdown(false);
      setClienteSelecionado(null);
      setImovelSelecionado(null);
    }
  }, [isOpen, agendamento]);
  
  // Atualizar cliente/imóvel selecionado quando as listas forem carregadas
  useEffect(() => {
    if (clientesList.length > 0 && formData.clienteId && !clienteSelecionado) {
      const cliente = clientesList.find(c => c.id === parseInt(formData.clienteId));
      if (cliente) {
        setClienteSelecionado(cliente);
        setPesquisaCliente(`${cliente.nome}${cliente.cpfCnpj ? ` - ${cliente.cpfCnpj}` : ''}`);
      }
    }
  }, [clientesList, formData.clienteId]);
  
  useEffect(() => {
    if (imoveisList.length > 0 && formData.imovelId && !imovelSelecionado) {
      const imovel = imoveisList.find(i => i.id === parseInt(formData.imovelId));
      if (imovel) {
        setImovelSelecionado(imovel);
        setPesquisaImovel(`${imovel.codigo} - ${imovel.tipo} - ${imovel.bairro}, ${imovel.cidade}`);
      }
    }
  }, [imoveisList, formData.imovelId]);

  const loadClientes = async () => {
    try {
      setLoadingClientes(true);
      const response = await getClientes({});
      const clientes = response.data || [];
      setClientesList(clientes);
      
      // Se houver clienteId no formData, preencher automaticamente
      if (formData.clienteId && clientes.length > 0) {
        const cliente = clientes.find(c => c.id === parseInt(formData.clienteId));
        if (cliente) {
          setClienteSelecionado(cliente);
          setPesquisaCliente(`${cliente.nome}${cliente.cpfCnpj ? ` - ${cliente.cpfCnpj}` : ''}`);
        }
      }
    } catch (error) {
      console.error('Erro ao carregar clientes:', error);
    } finally {
      setLoadingClientes(false);
    }
  };

  const loadImoveis = async () => {
    try {
      setLoadingImoveis(true);
      const response = await getImoveis({});
      const imoveis = response.data || [];
      setImoveisList(imoveis);
      
      // Se houver imovelId no formData, preencher automaticamente
      if (formData.imovelId && imoveis.length > 0) {
        const imovel = imoveis.find(i => i.id === parseInt(formData.imovelId));
        if (imovel) {
          setImovelSelecionado(imovel);
          setPesquisaImovel(`${imovel.codigo} - ${imovel.tipo} - ${imovel.bairro}, ${imovel.cidade}`);
        }
      }
    } catch (error) {
      console.error('Erro ao carregar imóveis:', error);
    } finally {
      setLoadingImoveis(false);
    }
  };

  // Filtrar clientes baseado na pesquisa
  const clientesFiltrados = pesquisaCliente
    ? clientesList.filter(cliente => {
        const pesquisa = pesquisaCliente.toLowerCase();
        const nome = (cliente.nome || '').toLowerCase();
        const cpfCnpj = (cliente.cpfCnpj || '').toLowerCase();
        return nome.includes(pesquisa) || cpfCnpj.includes(pesquisa);
      })
    : clientesList;

  // Filtrar imóveis baseado na pesquisa
  const imoveisFiltrados = pesquisaImovel
    ? imoveisList.filter(imovel => {
        const pesquisa = pesquisaImovel.toLowerCase();
        const codigo = (imovel.codigo || '').toLowerCase();
        const tipo = (imovel.tipo || '').toLowerCase();
        const bairro = (imovel.bairro || '').toLowerCase();
        const cidade = (imovel.cidade || '').toLowerCase();
        return codigo.includes(pesquisa) || tipo.includes(pesquisa) || 
               bairro.includes(pesquisa) || cidade.includes(pesquisa);
      })
    : imoveisList;

  const handleSelectCliente = (cliente) => {
    setClienteSelecionado(cliente);
    setFormData({ ...formData, clienteId: cliente.id });
    setPesquisaCliente(`${cliente.nome}${cliente.cpfCnpj ? ` - ${cliente.cpfCnpj}` : ''}`);
    setShowClienteDropdown(false);
  };

  const handleSelectImovel = (imovel) => {
    setImovelSelecionado(imovel);
    setFormData({ ...formData, imovelId: imovel.id });
    setPesquisaImovel(`${imovel.codigo} - ${imovel.tipo} - ${imovel.bairro}, ${imovel.cidade}`);
    setShowImovelDropdown(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (saving) return; // Prevenir cliques duplos
    if (!formData.clienteId || !formData.imovelId || !formData.dataHora) {
      alert('Por favor, preencha todos os campos obrigatórios');
      return;
    }
    
    // Validar se os IDs são válidos (não podem ser strings vazias ou números simples)
    if (formData.clienteId === '' || formData.imovelId === '') {
      alert('Por favor, selecione um cliente e um imóvel válidos');
      return;
    }
    
    setSaving(true);
    
    // Converter dataHora para o formato esperado pelo backend
    // O input datetime-local retorna no formato YYYY-MM-DDTHH:mm (sem timezone)
    // Precisamos extrair data e hora diretamente sem conversão para UTC
    const dataHoraStr = formData.dataHora; // Formato: YYYY-MM-DDTHH:mm
    const [dataPart, horaPart] = dataHoraStr.split('T');
    const data_visita = dataPart; // YYYY-MM-DD (já está no formato correto)
    const horario_visita = horaPart.slice(0, 5); // HH:MM (já está no formato correto)
    
    // Converter para o formato esperado pelo backend
    const dadosParaBackend = {
      cliente_id: parseInt(formData.clienteId),
      imovel_id: parseInt(formData.imovelId),
      data_visita: data_visita,
      horario_visita: horario_visita,
      status: formData.status || 'agendado',
      observacoes: formData.observacoes && formData.observacoes.trim() ? formData.observacoes.trim() : null
    };
    
    try {
      await onSave(dadosParaBackend, agendamento?.id);
    } catch (error) {
      console.error('Erro ao salvar agendamento:', error);
      alert('Erro ao salvar agendamento. Tente novamente.');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="agendamento-modal-overlay" onClick={(e) => {
      if (e.target === e.currentTarget) {
        setShowClienteDropdown(false);
        setShowImovelDropdown(false);
        onClose();
      }
    }}>
      <div className="agendamento-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="agendamento-modal-header">
          <h2>{agendamento ? 'Editar Agendamento' : 'Novo Agendamento'}</h2>
          <button className="agendamento-modal-close" onClick={onClose}>
            <FaTimes />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="agendamento-form">
          <div className="form-group">
            <label>Cliente *</label>
            {loadingClientes ? (
              <div className="loading-select">Carregando clientes...</div>
            ) : (
              <div className="search-input-wrapper">
                <input
                  type="text"
                  value={pesquisaCliente}
                  onChange={(e) => {
                    setPesquisaCliente(e.target.value);
                    setShowClienteDropdown(true);
                    if (!e.target.value) {
                      setClienteSelecionado(null);
                      setFormData({ ...formData, clienteId: '' });
                    }
                  }}
                  onFocus={() => setShowClienteDropdown(true)}
                  placeholder="Digite para buscar cliente..."
                  className="search-input"
                  required={!clienteSelecionado}
                />
                {showClienteDropdown && pesquisaCliente && clientesFiltrados.length > 0 && (
                  <div className="search-dropdown">
                    {clientesFiltrados.slice(0, 10).map(cliente => (
                      <div
                        key={cliente.id}
                        className="search-dropdown-item"
                        onClick={() => handleSelectCliente(cliente)}
                      >
                        <div className="search-item-name">{cliente.nome}</div>
                        {cliente.cpfCnpj && (
                          <div className="search-item-detail">{cliente.cpfCnpj}</div>
                        )}
                      </div>
                    ))}
                    {clientesFiltrados.length > 10 && (
                      <div className="search-dropdown-item search-dropdown-info">
                        Mostrando 10 de {clientesFiltrados.length} resultados
                      </div>
                    )}
                  </div>
                )}
                {showClienteDropdown && pesquisaCliente && clientesFiltrados.length === 0 && (
                  <div className="search-dropdown">
                    <div className="search-dropdown-item search-dropdown-info">
                      Nenhum cliente encontrado
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="form-group">
            <label>Imóvel *</label>
            {loadingImoveis ? (
              <div className="loading-select">Carregando imóveis...</div>
            ) : (
              <div className="search-input-wrapper">
                <input
                  type="text"
                  value={pesquisaImovel}
                  onChange={(e) => {
                    setPesquisaImovel(e.target.value);
                    setShowImovelDropdown(true);
                    if (!e.target.value) {
                      setImovelSelecionado(null);
                      setFormData({ ...formData, imovelId: '' });
                    }
                  }}
                  onFocus={() => setShowImovelDropdown(true)}
                  placeholder="Digite código, tipo, bairro ou cidade..."
                  className="search-input"
                  required={!imovelSelecionado}
                />
                {showImovelDropdown && pesquisaImovel && imoveisFiltrados.length > 0 && (
                  <div className="search-dropdown">
                    {imoveisFiltrados.slice(0, 10).map(imovel => (
                      <div
                        key={imovel.id}
                        className="search-dropdown-item"
                        onClick={() => handleSelectImovel(imovel)}
                      >
                        <div className="search-item-name">
                          {imovel.codigo} - {imovel.tipo}
                        </div>
                        <div className="search-item-detail">
                          {imovel.bairro}, {imovel.cidade}
                        </div>
                      </div>
                    ))}
                    {imoveisFiltrados.length > 10 && (
                      <div className="search-dropdown-item search-dropdown-info">
                        Mostrando 10 de {imoveisFiltrados.length} resultados
                      </div>
                    )}
                  </div>
                )}
                {showImovelDropdown && pesquisaImovel && imoveisFiltrados.length === 0 && (
                  <div className="search-dropdown">
                    <div className="search-dropdown-item search-dropdown-info">
                      Nenhum imóvel encontrado
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="form-group">
            <label>Data e Hora *</label>
            <input
              type="datetime-local"
              value={formData.dataHora}
              onChange={(e) => setFormData({ ...formData, dataHora: e.target.value })}
              required
            />
            <small>Formato: DD/MM/AAAA HH:MM (horário do Brasil)</small>
          </div>

          <div className="form-group">
            <label>Status</label>
            <select
              value={formData.status}
              onChange={(e) => setFormData({ ...formData, status: e.target.value })}
            >
              <option value="agendado">Agendado</option>
              <option value="confirmado">Confirmado</option>
              <option value="realizado">Realizado</option>
              <option value="cancelado">Cancelado</option>
              <option value="remarcado">Remarcado</option>
            </select>
          </div>

          <div className="form-group">
            <label>Observações</label>
            <textarea
              value={formData.observacoes}
              onChange={(e) => setFormData({ ...formData, observacoes: e.target.value })}
              rows="4"
              placeholder="Observações sobre a visita..."
            />
          </div>

          <div className="form-actions">
            <button type="button" className="btn-cancel" onClick={onClose} disabled={saving}>
              Cancelar
            </button>
            <button type="submit" className="btn-save" disabled={saving}>
              {saving ? 'Salvando...' : (agendamento ? 'Atualizar' : 'Salvar')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AgendamentoModal;

